/* ════════════════════════════════════════════════════════════
   SPA portfolio renderer.
   Builds the #cover-stack from PORTFOLIO_PROJECTS (awaits
   window.__projectsReady, which is set by an inline bootstrap
   in public/index.html), preloads covers, wires BorderGlow
   hover effects + meta-block, and runs the bottom-bar clock.
   Cover anchors use real /work/<slug> hrefs so the router's
   global click handler intercepts them.
   No public window.* API.
   ════════════════════════════════════════════════════════════ */
'use strict';

function escapeAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const stack = document.getElementById('cover-stack');

/* Wait for project data (fetched by the bootstrap script in
   public/index.html) before rendering the cover stack. The clock
   at the bottom of this module runs independently and doesn't
   depend on projects. */
if (stack) (window.__projectsReady || Promise.resolve([])).then(function (PORTFOLIO_PROJECTS) {

  /* Preload every cover image immediately, regardless of whether
     the portfolio view is currently shown. This guarantees they're
     in the HTTP cache (and decoded) before the slide animation
     reveals them. */
  PORTFOLIO_PROJECTS.forEach(function (p) {
    const i = new Image();
    i.decoding = 'async';
    i.src = p.img;
  });

  /* Cover anchors use real /work/<slug> hrefs so the router's
     global click handler intercepts them via navigateTo. */
  stack.innerHTML = PORTFOLIO_PROJECTS.map(function (p) {
    return '<div class="cover-wrapper">'
         +   '<span class="edge-light"></span>'
         +   '<a class="cover-item" href="/work/' + escapeAttr(p.slug) + '"'
         +     ' data-project="' + escapeAttr(p.project) + '"'
         +     ' data-company="' + escapeAttr(p.company) + '"'
         +     ' data-type="'    + escapeAttr(p.type)    + '"'
         +     ' data-year="'    + escapeAttr(p.year)    + '">'
         +     '<img src="' + escapeAttr(p.img) + '"'
         +     ' alt="' + escapeAttr(p.project) + '"'
         +     ' loading="eager" decoding="async">'
         +   '</a>'
         + '</div>';
  }).join('');

  /* Force a synchronous layout pass on the cover stack right
     after mounting, so the browser allocates layout boxes for
     every wrapper / image before the user navigates away. */
  void stack.offsetHeight;

  /* === BorderGlow math === */
  function getEdgeProximity(el, x, y) {
    const r = el.getBoundingClientRect();
    const cx = r.width / 2, cy = r.height / 2;
    const dx = x - cx, dy = y - cy;
    const kx = dx !== 0 ? cx / Math.abs(dx) : Infinity;
    const ky = dy !== 0 ? cy / Math.abs(dy) : Infinity;
    return Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
  }
  function getCursorAngle(el, x, y) {
    const r = el.getBoundingClientRect();
    const dx = x - r.width / 2, dy = y - r.height / 2;
    if (!dx && !dy) return 0;
    let deg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    return deg < 0 ? deg + 360 : deg;
  }
  function getPrecedingSiblings(el) {
    const out = [];
    let p = el.previousElementSibling;
    while (p) { out.push(p); p = p.previousElementSibling; }
    return out;
  }

  const metaBlock   = document.getElementById('meta-block');
  const metaProject = document.getElementById('meta-project');
  const metaCompany = document.getElementById('meta-company');
  const metaType    = document.getElementById('meta-type');
  const metaYear    = document.getElementById('meta-year');
  let hideTimer;

  /* ── Mobile cover-flow: reverse the visual order so the user
     lands on the most recent project first (mirroring desktop, where
     the newest sits in front of the stack). We reorder DOM children
     before the IntersectionObserver attaches; on desktop the order
     is unchanged so :nth-child Z-depth rules still apply. */
  if (window.matchMedia('(max-width: 768px)').matches) {
    const reorderTarget = stack;
    const wrappersToReverse = Array.from(reorderTarget.querySelectorAll('.cover-wrapper'));
    for (let i = wrappersToReverse.length - 1; i >= 0; i--) {
      reorderTarget.appendChild(wrappersToReverse[i]);
    }
  }

  /* ── Crossfade-capable metadata writer (desktop + mobile).
     Builds a ghost layer that mirrors #meta-block's structure so we
     can swap text between two stacked layers with opposite opacity
     transitions. Used by:
       - desktop hover handlers (mouseenter on a cover-item)
       - mobile IntersectionObserver (cover centered in stack)
     The block also has a parent-level .visible class (legacy):
     show/hide for desktop "no card hovered" state. */
  let ghostLayer = null;
  let ghostFields = null;
  const mainFields = {
    year: metaYear, project: metaProject, company: metaCompany, type: metaType,
  };
  if (metaBlock && metaBlock.parentNode) {
    ghostLayer = document.createElement('div');
    ghostLayer.className = 'meta-ghost';
    ghostLayer.setAttribute('aria-hidden', 'true');
    ghostLayer.innerHTML =
      '<div class="meta-year"></div>' +
      '<div class="meta-project"></div>' +
      '<div class="meta-company-type">' +
        '<span class="meta-company"></span>' +
        '<span class="meta-type"></span>' +
      '</div>';
    metaBlock.parentNode.insertBefore(ghostLayer, metaBlock.nextSibling);
    ghostFields = {
      year:    ghostLayer.querySelector('.meta-year'),
      project: ghostLayer.querySelector('.meta-project'),
      company: ghostLayer.querySelector('.meta-company'),
      type:    ghostLayer.querySelector('.meta-type'),
    };
  }

  function setFields(target, dataset) {
    if (target.project) target.project.textContent = dataset.project || '';
    if (target.company) target.company.textContent = dataset.company || '';
    if (target.type)    target.type.textContent    = dataset.type    || '';
    if (target.year)    target.year.textContent    = dataset.year    || '';
  }

  /* Tracks which layer is currently the "visible" one. After every
     swap we flip this so the previously-hidden layer becomes the
     canonical visible layer (no DOM swap, just opacity-state swap). */
  let visibleLayer = 'main'; // 'main' = #meta-block, 'ghost' = .meta-ghost
  let lastMetaKey = '';

  function writeMeta(dataset) {
    if (!dataset) return;
    const key = (dataset.project || '') + '|' + (dataset.year || '');

    // First paint: write to main, no fade. Caller (hover/observer)
    // is responsible for adding .visible to make the block appear.
    if (!lastMetaKey) {
      setFields(mainFields, dataset);
      lastMetaKey = key;
      return;
    }
    if (key === lastMetaKey) return;
    lastMetaKey = key;

    // No ghost (defensive) — direct write.
    if (!ghostLayer) { setFields(mainFields, dataset); return; }

    if (visibleLayer === 'main') {
      // Main is visible — populate ghost with new content, then
      // crossfade: main 1→0 while ghost 0→1, simultaneously.
      setFields(ghostFields, dataset);
      void ghostLayer.offsetWidth; // commit text before transition
      metaBlock.classList.add('meta-fading');
      ghostLayer.classList.add('meta-fading-in');
      visibleLayer = 'ghost';
    } else {
      // Ghost is visible — populate main with new content, then
      // crossfade in the other direction.
      setFields(mainFields, dataset);
      void metaBlock.offsetWidth;
      metaBlock.classList.remove('meta-fading');
      ghostLayer.classList.remove('meta-fading-in');
      visibleLayer = 'main';
    }
  }

  /* Show/hide both layers in sync (desktop reveal on hover, dismiss
     on leave). On mobile the block stays visible permanently. */
  function setMetaVisible(visible) {
    if (!metaBlock) return;
    metaBlock.classList.toggle('visible', visible);
    if (ghostLayer) ghostLayer.classList.toggle('visible', visible);
  }

  /* ── Mobile cover-flow: drive #meta-block from the centered card.
     On touch viewports the stack is a horizontal snap-scroller (see
     the @media (max-width: 768px) block in public/index.html). We
     use an IntersectionObserver with the stack as root and a tight
     horizontal margin so only the card crossing the viewport center
     is "intersecting"; that card becomes .is-active and its data-*
     attrs populate the always-visible metadata at the top. */
  const isMobilePortfolio = window.matchMedia('(max-width: 768px)').matches;
  if (isMobilePortfolio) {
    const wrappers = Array.from(stack.querySelectorAll('.cover-wrapper'));

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            wrappers.forEach(function (w) { w.classList.remove('is-active'); });
            entry.target.classList.add('is-active');
            const item = entry.target.querySelector('.cover-item');
            if (item) writeMeta(item.dataset);
          }
        });
      }, {
        root: stack,
        /* Inset 45% on each side so only the card whose center is
           within the middle 10% of the stack counts as intersecting. */
        rootMargin: '0px -45% 0px -45%',
        threshold: 0,
      });
      wrappers.forEach(function (w) { io.observe(w); });
    }

    /* Seed the first card as active + populate meta before the
       observer fires its first callback. */
    if (wrappers[0]) {
      wrappers[0].classList.add('is-active');
      const firstItem = wrappers[0].querySelector('.cover-item');
      if (firstItem) writeMeta(firstItem.dataset);
      setMetaVisible(true);
    }

    /* Re-show meta whenever the portfolio view becomes visible again
       (e.g. after closing a case study). The IO observer doesn't
       refire when the stack scroll position is unchanged, so the
       meta-block's .visible class can get out of sync. We watch the
       portfolio view's display style and re-assert visibility +
       repopulate from whichever card is currently .is-active. */
    /* Repopulate + force-show the meta block from whichever cover is
       currently .is-active. Idempotent. Used by the MutationObserver
       on the portfolio view AND by the mobile FLIP close path (which
       calls this directly because the MO may not fire reliably when
       inline display goes from 'none' back to ''). */
    function refreshMetaFromActiveCover() {
      const active = stack.querySelector('.cover-wrapper.is-active');
      const item = active && active.querySelector('.cover-item');
      /* Belt-and-suspenders: strip .is-cs-transitioning from the
         view. The CSS rule
           #view-portfolio.is-cs-transitioning #meta-block { opacity:0 !important }
         pins both meta layers invisible. If a previous FLIP path
         left this class on (rare on mobile, but possible if a
         close path errored out mid-flight), every subsequent
         refresh would do the right thing class-wise but stay
         invisible due to !important. */
      const portfolioView = document.getElementById('view-portfolio');
      if (portfolioView) portfolioView.classList.remove('is-cs-transitioning');
      if (item) {
        /* Reset crossfade state completely. If writeMeta was
           mid-fade when the user opened the case study, .meta-fading
           is still on #meta-block and .meta-fading-in still on
           .meta-ghost — so when we come back, main is at opacity 0
           and ghost is at opacity 1 with stale text. */
        if (metaBlock)  metaBlock.classList.remove('meta-fading');
        if (ghostLayer) ghostLayer.classList.remove('meta-fading-in');
        visibleLayer = 'main';
        lastMetaKey = '';
        writeMeta(item.dataset);
      }
      setMetaVisible(true);
    }
    window.__portfolioMetaRefresh = refreshMetaFromActiveCover;

    /* Watch the portfolio view's inline display: when it flips back from
       'none' to '' (returning from a case study), re-assert metadata
       visibility from the currently active cover. The mobile FLIP close
       path also calls __portfolioMetaRefresh() directly because this
       observer can miss the mutation on iOS Safari. */
    if (typeof MutationObserver === 'function') {
      const portfolioView = document.getElementById('view-portfolio');
      if (portfolioView) {
        const mo = new MutationObserver(function () {
          if (portfolioView.style.display !== 'none') {
            refreshMetaFromActiveCover();
          }
        });
        mo.observe(portfolioView, { attributes: true, attributeFilter: ['style'] });
      }
    }
  }

  stack.querySelectorAll('.cover-wrapper').forEach(function (wrapper) {
    const item = wrapper.querySelector('.cover-item');
    if (!item) return;

    const portfolioEl = stack.closest('#view-portfolio');
    function isFrozen() { return portfolioEl && portfolioEl.classList.contains('covers-frozen'); }

    wrapper.addEventListener('mouseenter', function () {
      if (isFrozen()) return;
      getPrecedingSiblings(wrapper).forEach(function (s) { s.classList.add('push-back'); });
    });
    wrapper.addEventListener('mouseleave', function () {
      if (isFrozen()) return;
      getPrecedingSiblings(wrapper).forEach(function (s) { s.classList.remove('push-back'); });
    });
    wrapper.addEventListener('pointermove', function (e) {
      if (isFrozen()) return;
      const r = wrapper.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      wrapper.style.setProperty('--edge-proximity', (getEdgeProximity(wrapper, x, y) * 100).toFixed(2));
      wrapper.style.setProperty('--cursor-angle', getCursorAngle(wrapper, x, y).toFixed(2) + 'deg');
    });
    wrapper.addEventListener('pointerleave', function () {
      wrapper.style.setProperty('--edge-proximity', '0');
    });
    /* Desktop-only meta show/hide. On touch viewports iOS Safari fires
       synthetic mouseenter/mouseleave on tap, which would strip the
       .visible class from the meta layers (the mobile path keeps them
       permanently visible and drives content from the IntersectionObserver
       above). Gating on isMobilePortfolio prevents the disappearance on
       return from a case study. */
    if (!isMobilePortfolio) {
      item.addEventListener('mouseenter', function () {
        clearTimeout(hideTimer);
        writeMeta(item.dataset);
        setMetaVisible(true);
      });
      item.addEventListener('mouseleave', function () {
        hideTimer = setTimeout(function () {
          setMetaVisible(false);
        }, 80);
      });
    }
  });

});  /* end __projectsReady.then for cover-stack render */

/* === Bottom-bar live clock — runs unconditionally,
   doesn't depend on projects. === */
function updateClock() {
  const dateEl = document.getElementById('bottomBarDate');
  const timeEl = document.getElementById('bottomBarTime');
  if (!dateEl || !timeEl) return;
  const now = new Date();
  const days   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  dateEl.textContent = days[now.getDay()] + ' ' + months[now.getMonth()] + ' ' + now.getDate();
  const h = now.getHours();
  const m = String(now.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  timeEl.textContent = String(h12).padStart(2, '0') + ':' + m + ampm;
}
updateClock();
setInterval(updateClock, 1000);
